<script lang="ts">
  // The Strava Sync page (/strava, StravaController@index): connect or disconnect Strava, choose
  // what syncs automatically and which activity types, what Xert shares, and import past Strava
  // activities.
  import { Button } from '$lib/components/ui/button'
  import * as Card from '$lib/components/ui/card'
  import * as Dialog from '$lib/components/ui/dialog'
  import { Switch } from '$lib/components/ui/switch'
  import SettingSwitch from '@/Common/SettingSwitch.svelte'
  import ActivityTypes from '@/Integration/Sync/ActivityTypes.svelte'
  import ActivityTypesDialog from '@/Integration/Sync/ActivityTypesDialog.svelte'
  import ConnectButton from '@/Integration/Sync/ConnectButton.svelte'
  import SyncConnection from '@/Integration/Sync/SyncConnection.svelte'
  import SyncHelp from '@/Integration/Sync/SyncHelp.svelte'
  import SyncHelpItem from '@/Integration/Sync/SyncHelpItem.svelte'
  import GroupReportPrivacy from '@/SocialReports/GroupReportPrivacy.svelte'
  import { saveStravaActivityTypes, saveStravaAutoSync, saveStravaAutoUpdate, type BasicResponse } from '@/Util/api'
  import { ErrorCode } from '@/Util/errorCodes'
  import { notifyApiError } from '@/Util/notify'
  import { focusMainButton } from '@/Util/utils'
  import StravaImport from './StravaImport.svelte'
  import { typeLabel } from './strava'

  export let connected: boolean
  /** The connection can't refresh its access, so Strava has to be connected again. */
  export let needsReconnect = false
  export let autoUpdate: boolean
  export let syncToXert: boolean
  export let syncToStrava: boolean
  export let showOnGroupReports: boolean
  /** The sport's own types, always synced. */
  export let defaultTypes: string[]
  /** Other types the athlete can add. */
  export let typeOptions: { value: string, checked: boolean }[]
  export let distanceUnits: string
  export let canSync: boolean

  const authorizeUrl = '/strava/authorize'
  const disconnectUrl = '/strava/disconnect'

  $: chosenTypes = typeOptions.filter((t) => t.checked).map((t) => t.value)
  let typesOpen = false
  let connectOpen = false
  // Asked before connecting, like the original page, when reports aren't on yet.
  let connectWithReports = true
  let connecting = false

  function saved(res: BasicResponse, message: string) {
    if (res.success) return true
    notifyApiError(res, { code: ErrorCode.STRAVA_SETTING_FAILED, message })
    return false
  }

  const saveToXert = async (on: boolean) =>
    saved(await saveStravaAutoSync(on, syncToStrava), 'Could not save your automatic sync setting. Please try again.')
  const saveToStrava = async (on: boolean) =>
    saved(await saveStravaAutoSync(syncToXert, on), 'Could not save your automatic sync setting. Please try again.')
  const saveAutoUpdate = async (on: boolean) =>
    saved(await saveStravaAutoUpdate(on), 'Could not save your Strava report setting. Please try again.')
  const saveTypes = async (types: string[]) =>
    saved(await saveStravaActivityTypes(types), 'Could not save your activity types. Please try again.')

  function connect() {
    if (autoUpdate) {
      window.location.href = authorizeUrl
      return
    }
    connectWithReports = true
    connectOpen = true
    focusMainButton()
  }

  async function continueToStrava() {
    connecting = true
    if (connectWithReports && !(await saveAutoUpdate(true))) {
      connecting = false
      return
    }
    autoUpdate = connectWithReports
    window.location.href = authorizeUrl
  }
</script>

<div class="mx-auto flex max-w-[1100px] flex-col gap-4 px-4 pb-8">
  <SyncConnection service="strava" {connected} {disconnectUrl}
    benefits={[
      'Import your Strava activities into Xert, automatically or from your history',
      'Send activities you record with Xert to Strava',
      'Add breakthrough, workout and group reports to your Strava activities',
    ]}
    warning={connected && needsReconnect ? 'Your Strava connection needs renewing. Disconnect Strava, then connect again to keep your activities syncing.' : ''}
    disconnectMessage="Xert stops importing your Strava activities and sending activities to Strava. Activities already in Xert stay.">
    <ConnectButton slot="connect" service="strava" on:click={connect} />
  </SyncConnection>

  <div class="grid gap-4 lg:grid-cols-2">
    {#if connected}
      <Card.Root>
        <Card.Header class="pb-0">
          <Card.Title class="text-base">Automatic sync</Card.Title>
          <Card.Description>New activities sync as soon as they're recorded.</Card.Description>
        </Card.Header>
        <Card.Content class="divide-y divide-border">
          <SettingSwitch id="sync-to-xert" bind:checked={syncToXert} save={saveToXert}
            label="Import new Strava activities into Xert"
            description="Activities of the types below are added to Xert when you upload them to Strava." />
          <SettingSwitch id="sync-to-strava" bind:checked={syncToStrava} save={saveToStrava}
            label="Send new Xert activities to Strava"
            description="Activities you record or upload in Xert are sent to Strava." />
          <ActivityTypes {defaultTypes} {chosenTypes} label={typeLabel} on:click={() => (typesOpen = true)} />
        </Card.Content>
      </Card.Root>
    {/if}

    <Card.Root class={connected ? '' : 'lg:col-span-2'}>
      <Card.Header class="pb-0">
        <Card.Title class="text-base">Sharing</Card.Title>
        <Card.Description>What Xert adds to Strava, and how you appear to other riders.</Card.Description>
      </Card.Header>
      <Card.Content class="divide-y divide-border">
        <SettingSwitch id="strava-auto-update" bind:checked={autoUpdate} save={saveAutoUpdate}
          label="Show reports and summary data on Strava"
          description="Adds breakthrough, workout and group session reports and a summary to activities Xert sends to Strava. ">
          <a href="/social-reports" class="font-medium text-primary underline-offset-2 hover:underline">Design your reports</a>
        </SettingSwitch>
        <GroupReportPrivacy bind:checked={showOnGroupReports} />
      </Card.Content>
    </Card.Root>
  </div>

  {#if connected}
    <StravaImport types={chosenTypes} {distanceUnits} {canSync} />
  {/if}

  <SyncHelp>
    <SyncHelpItem title="Syncing problems?">Disconnect Strava, connect again and allow every permission Strava asks for.</SyncHelpItem>
    <SyncHelpItem title="Reports not showing on Strava?">
      Turn on <em>Show reports and summary data</em>, then <a href="https://baronbiosys.com/faq-items/how-do-i-get-breakthrough-and-workout-reports-to-show-on-strava" target="_blank" rel="noopener">follow these steps</a>.
    </SyncHelpItem>
    <SyncHelpItem title="Missing Garmin activities?">Check your connection on the <a href="/garmin-health/sync">Garmin Connect Sync page</a>.</SyncHelpItem>
  </SyncHelp>
</div>

{#if connected}
  <ActivityTypesDialog id="strava" serviceName="Strava" bind:open={typesOpen} {defaultTypes} bind:typeOptions label={typeLabel} requireOne={false} save={saveTypes} />
{/if}

<Dialog.Root portal="#shadcn-ui-dialog-target" bind:open={connectOpen}>
  <Dialog.Content class="max-w-md">
    <Dialog.Header>
      <Dialog.Title>Connect with Strava</Dialog.Title>
      <Dialog.Description>
        Strava will ask you to allow Xert access. Allow every permission so activities can sync both ways.
      </Dialog.Description>
    </Dialog.Header>
    <div class="flex items-start justify-between gap-4 rounded-lg border border-border p-3">
      <label for="connect-reports" class="m-0 flex cursor-pointer flex-col gap-0.5">
        <span class="text-sm font-medium text-foreground">Show breakthroughs and workouts on Strava</span>
        <span class="text-xs font-normal text-muted-foreground">Adds Xert reports to the activities Xert sends to Strava.</span>
      </label>
      <Switch id="connect-reports" bind:checked={connectWithReports} />
    </div>
    <Dialog.Footer class="gap-2">
      <Button variant="outline" on:click={() => (connectOpen = false)}>Cancel</Button>
      <Button on:click={continueToStrava} disabled={connecting} data-main-button>
        {#if connecting}<i class="fa-solid fa-spinner-third fa-spin mr-2" aria-hidden="true" />{/if}Continue to Strava
      </Button>
    </Dialog.Footer>
  </Dialog.Content>
</Dialog.Root>
